import type { MediaAssetResponse } from '@/core/generated/api';
import { formatMediaDimensions, formatMediaSize, formatMediaType } from '@/entities/MediaLibrary/model/format';
import { ActionMenu } from '@/shared/ui/ActionMenu/ActionMenu';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { useImageDimensions } from '@/shared/hooks/useImageDimensions';
import classes from './MediaLibrary.module.scss';

interface MediaItemsProps {
    items: MediaAssetResponse[];
    selected: string[];
    list: boolean;
    onSelect: (id: string) => void;
    onOpen: (item: MediaAssetResponse) => void;
    actions: (item: MediaAssetResponse) => ActionMenuItem[];
}

/** Показывает карточки или строки изображений медиатеки */
export const MediaItems = (
    { items, selected, list, onSelect, onOpen, actions }: MediaItemsProps
) => {
    const { recordDimensions, getDimensions } = useImageDimensions();

    return (
        <div
            className={list
                ? classes.list
                : classes.grid
            }
            aria-label="Файлы медиатеки"
        >
            {list &&
                <div className={classes.tableHeading}>
                    <span>Название</span>
                    <span>Тип</span>
                    <span>Размер</span>
                    <span>Добавлен</span>
                    <span>Использование</span>
                </div>
            }
            {items.map(item => {
                const dimensions = getDimensions(item.url, item.width, item.height);

                return <article
                    key={item.id}
                    className={`${classes.card} ${selected.includes(item.id ?? '')
                        ? classes.selected
                        : ''}`}
                >
                    <div className={classes.thumbnail}>
                        <button
                            type="button"
                            className={classes.previewButton}
                            onClick={() => onOpen(item)}
                            aria-label={`Открыть ${item.name}`}
                        >
                            <img
                                src={item.url ?? ''}
                                alt={item.name ?? 'Изображение'}
                                loading="lazy"
                                onLoad={event => recordDimensions(item.url, event.currentTarget)}
                            />
                        </button>
                        <input
                            className={classes.check}
                            type="checkbox"
                            aria-label={`Выбрать ${item.name}`}
                            checked={selected.includes(item.id ?? '')}
                            onChange={() => onSelect(item.id ?? '')}
                        />
                        {item.isUsed &&
                            <span className={classes.used}>
                                <i
                                    aria-hidden="true"
                                    className="pi pi-check-circle"
                                /> Используется</span>
                        }
                    </div>
                    <div className={classes.cardInfo}>
                        <button
                            type="button"
                            className={classes.name}
                            onClick={() => onOpen(item)}
                        >
                            {item.name}
                        </button>
                        <div className={classes.metadata}>
                            <span>
                                {formatMediaType(item.contentType)}
                            </span>
                            <span>
                                {formatMediaSize(item.size)}
                            </span>
                            <span className={classes.dimensions}>
                                {formatMediaDimensions(dimensions?.width, dimensions?.height)}
                            </span>
                        </div>
                        {Boolean(item.tags?.length) &&
                            <div className={classes.tags}>
                                {item.tags?.map(tag => <span key={tag}>
                                    {tag}
                                </span>)}
                            </div>
                        }
                    </div>
                    {list &&
                        <>
                            <span>
                                {formatMediaType(item.contentType)}
                            </span>
                            <span>
                                {formatMediaSize(item.size)}
                                <small className={classes.listDimensions}>
                                    {formatMediaDimensions(dimensions?.width, dimensions?.height)}
                                </small>
                            </span>
                            <span>
                                {item.created
                                    ? new Date(item.created).toLocaleDateString('ru-RU')
                                    : '—'}
                            </span>
                            <span>
                                {item.usageChecked === false
                                    ? 'См. сведения о файле'
                                    : item.isUsed
                                        ? 'Используется'
                                        : 'Не используется'}
                            </span>
                        </>
                    }
                    <div className={classes.cardActions}>
                        <ActionMenu
                            aria-label={`Действия ${item.name}`}
                            model={actions(item)}
                        />
                    </div>
                </article>;
            })}
        </div>
    );
};
